import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ExternalLink } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_REVIEW_URL =
  "https://www.google.com/maps/search/Top+Rate+Cash+for+Gold+Gurgaon";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

export default function Testimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, []);

  const avg =
    items.length > 0
      ? (
          items.reduce((s, t) => s + (t.rating || 5), 0) / items.length
        ).toFixed(1)
      : "5.0";

  return (
    <section
      data-testid={TESTIDS.testimonialsSection}
      className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
        <div className="lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
            Customer Voices · ग्राहक अनुभव
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
            <span className="italic text-[#641C24]">Trusted</span> across
            Gurgaon &amp; Delhi NCR.
          </h2>
        </div>

        {/* Google rating summary */}
        <div className="lg:col-span-5">
          <div
            data-testid="google-rating-card"
            className="bg-white border border-[#E5DCC5] shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <div className="flex items-center gap-4">
              <GoogleG />
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-4xl font-black text-[#2D181A] leading-none">
                    {avg}
                  </span>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={15} fill="#FBBC05" strokeWidth={0} />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-[#4A3B3C] mt-1">
                  Rated by our customers on Google
                </div>
              </div>
            </div>
            <a
              data-testid="google-review-link"
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-[#641C24]/50 hover:bg-[#641C24] hover:text-white text-[#641C24] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors sm:ml-auto shrink-0"
            >
              <ExternalLink size={13} /> Review us on Google
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {items.slice(0, 6).map((t) => (
          <div
            key={t.id}
            data-testid={TESTIDS.testimonialCard(t.id)}
            className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col"
          >
            <div className="flex items-center justify-between">
              <Quote size={26} strokeWidth={1.2} className="text-[#D4AF37]" />
              <GoogleG />
            </div>
            <div className="flex items-center gap-1 mt-4">
              {Array.from({ length: t.rating || 5 }).map((_, i) => (
                <Star key={i} size={14} fill="#FBBC05" strokeWidth={0} />
              ))}
            </div>
            <p className="text-sm text-[#2D181A] leading-relaxed mt-4 flex-1">
              “{t.message}”
            </p>
            <div className="mt-6 pt-4 border-t border-[#E5DCC5] flex items-center gap-3">
              <span className="w-9 h-9 shrink-0 bg-[#641C24] text-[#D4AF37] font-display font-bold flex items-center justify-center">
                {t.name?.charAt(0)}
              </span>
              <div>
                <div className="font-semibold text-[#2D181A] text-sm">
                  {t.name}
                </div>
                <div className="text-xs text-[#4A3B3C] uppercase tracking-[0.16em] mt-0.5">
                  {t.location}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
